import { ArrowUpRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NamedLink } from '../../components';
import MarketplaceCard from '../../components/MarketplaceCard/MarketplaceCard';
// Import product images used for the custom layout
import image5 from './images/image5.png';
import soldProduct1 from './images/sold-product1.png';
import soldProduct2 from './images/sold-product2.png';
import soldProduct3 from './images/sold-product3.png';
import soldProduct4 from './images/sold-product4.png';
import buttonCss from '../LandingPage/components/HomepageButtons.module.css';

import css from './SoldItemsSection.module.css';

const SOLD_ITEMS_LIMIT = 10;

const staticImages = [
  {
    id: 'static-sold-1',
    imageUrl: soldProduct1,
    title: 'Mario Valentino Juliette Shoulder Bag in Cognac',
    brandName: 'VALENTINO',
    price: { amount: 17500, currency: 'USD' },
  },
  {
    id: 'static-sold-2',
    imageUrl: soldProduct2,
    title: 'Blue and Brown Suede Round-Toe Pump',
    brandName: 'HERMES',
    price: { amount: 17500, currency: 'USD' },
  },
  {
    id: 'static-sold-3',
    imageUrl: soldProduct3,
    title: 'Clic H Panoplie Equestre bracelet',
    brandName: 'HERMES',
    price: { amount: 17500, currency: 'USD' },
  },
  {
    id: 'static-sold-4',
    imageUrl: soldProduct4,
    title: 'Patricia Washed Satin Wrap Red Dress',
    brandName: 'PAUL & JOE',
    price: { amount: 17500, currency: 'USD' },
  },
];

const getSoldItemsForPreview = (items, limit) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  return items.slice(0, limit);
};

const SoldItemsSection = ({ fetchSoldItemsInProgress, soldItems }) => {
  const gridRef = useRef(null);
  const wrapRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const soldItemsToRender = getSoldItemsForPreview(
    soldItems.length > 0 ? soldItems : staticImages,
    SOLD_ITEMS_LIMIT
  );

  useEffect(() => {
    const wrapEl = wrapRef.current;
    const gridEl = gridRef.current;
    if (!gridEl || !wrapEl) {
      return undefined;
    }

    const updateOverflowState = () => {
      const itemEls = Array.from(gridEl.children);
      if (itemEls.length === 0) {
        setHasOverflow(false);
        return;
      }

      const firstRect = itemEls[0].getBoundingClientRect();
      const lastRect = itemEls[itemEls.length - 1].getBoundingClientRect();
      const contentSpanWidth = lastRect.right - firstRect.left;
      const measuredContentWidth = Math.max(gridEl.scrollWidth, contentSpanWidth);
      const containerWidth = wrapEl.clientWidth;
      const isOverflowing = measuredContentWidth - containerWidth > 1;

      setHasOverflow(isOverflowing);
    };

    updateOverflowState();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            updateOverflowState();
          })
        : null;

    if (resizeObserver) {
      resizeObserver.observe(wrapEl);
      resizeObserver.observe(gridEl);
      Array.from(gridEl.children).forEach(child => resizeObserver.observe(child));
    }

    window.addEventListener('resize', updateOverflowState);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateOverflowState);
    };
  }, [soldItemsToRender]);

  return (
    <section className={css.soldItemsSection}>
      <div className={css.soldItemsContainer}>
        <h2 className={css.soldItemsTitle}>Someone beat you to it!</h2>

        {fetchSoldItemsInProgress && soldItems.length === 0 ? (
          <div className={css.productLoading}>Loading sold items…</div>
        ) : (
          <div
            ref={wrapRef}
            className={`${css.soldItemsGridWrap} ${
              !hasOverflow ? css.soldItemsGridWrapNoOverflow : ''
            }`}
          >
            <div
              ref={gridRef}
              className={`${css.soldItemsGrid} ${!hasOverflow ? css.soldItemsGridCentered : ''}`}
            >
              {soldItemsToRender.map((item, index) => (
                <MarketplaceCard
                  key={item.id || `sold-item-${index}`}
                  listing={{ ...item, imageUrl: item.imageUrl || image5 }}
                  variant="resale-sold"
                  size="small"
                  className={css.marketplaceCard}
                />
              ))}
            </div>
          </div>
        )}

        <div className={css.callToActionArea}>
          <p className={css.callToActionText}>
            That expensive mistake taking up closet real estate? Turn it into someone's holy grail
            find!
          </p>
          <NamedLink
            name="StartSellingSignupPage"
            className={`${css.startSellingButton} ${buttonCss.button} ${buttonCss.buttonPrimary} ${buttonCss.buttonDark}`}
          >
            <ArrowUpRight
              aria-hidden="true"
              className={`${css.startSellingArrow} ${buttonCss.icon}`}
              size={16}
              strokeWidth={2}
              absoluteStrokeWidth
            />
            <span>START SELLING</span>
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

export default SoldItemsSection;
